import * as React from 'react';
import { IUser } from '../Interfaces/IUser';
import { useTranslation } from 'react-i18next';
import i18n from '../../../Configs/i18n';
import {
  LanguagesContainer,
  LanguageSelector,
  LanguageSelectorSignup,
} from '../Styles/LanguageSelector';
import { getBaseUrlForRedirect } from '../../../__core/Utils/GetBaseUrl';
import { changeLanguage } from '../../../__core/Utils/Language';
import { setAppLang } from '../Actions/Actions';
import {
  Link,
  NavLink,
  RouteComponentProps,
  withRouter,
} from 'react-router-dom';

import {
  CorporateLogo,
  NavBar,
  NavbarIcon,
  SearchPortal,
  UserMenu,
  UserProfilePopOver,
  DesktopMenu,
  UserMenuLinkItem,
} from '../Styles/NavBar';
import AuthComponent from '../../../__core/Providers/Permissions/AuthComponent';
import ROLES from '../../../__core/Enums/Roles';
import { settingsIcon } from '../../../Assets/images/settings';
import UserProfile from './UserProfile/UserProfile';
import { profileIcon } from '../../../Assets/images/Icons';
import Popover from '../../../__core/Components/Tooltip/Popover';
import { GITHUB_ACCOUNT_LINKED } from '../../CourseInstance/Constraints';
import AppConfig from '../../../Configs/AppConfig';
import { LogoSrcProvider } from '../../../__core/Utils/LogoSrcProvider';
import { coursesIcon, directoryIcon } from '../../../Assets/images/TopBarIcons';
import { AppConfigType } from '../../../Configs/AppConfigType';
import { getToken } from '../../../__core/Providers/Token/GetToken';

type TopBarProps = {
  user: IUser;
  refreshUserData: () => void;
} & RouteComponentProps;

const TopBar: React.FunctionComponent<TopBarProps> = ({
  user,
  refreshUserData,
  location,
}): JSX.Element | null => {
  const { t } = useTranslation();
  const currentLanguage: string = i18n.language;
  const {
    TENANT_USER_LANGUAGES: tenantLangs = 'en',
    COURSES_CATALOG_URL = '',
  } = AppConfig as AppConfigType;
  const languages: Array<string> = tenantLangs.split(',');

  const getInitStateForSideMenu = (): boolean => {
    const { hash } = location;

    return hash === GITHUB_ACCOUNT_LINKED;
  };

  const onChangeLanguage = (lang: string): void => {
    changeLanguage(lang);
    if (getToken()) {
      setAppLang(lang);
    }
  };

  return (
    <NavBar id="top-bar-element">
      <CorporateLogo>
        {!!getToken() && user && !!user.id ? (
          <Link to="/courses">{LogoSrcProvider()}</Link>
        ) : (
          <a href="/">{LogoSrcProvider()}</a>
        )}
      </CorporateLogo>

      <SearchPortal id="search-portal" />

      {!getToken() && !!user && !user.id && (
        <div>
          <LanguagesContainer>
            {languages.map((lang) => (
              <LanguageSelectorSignup
                key={lang}
                id={`${lang}-lang-button`}
                isActive={lang === currentLanguage}
                onClick={() => onChangeLanguage(lang)}
              >
                {lang}
              </LanguageSelectorSignup>
            ))}
          </LanguagesContainer>
        </div>
      )}

      {!!user && !!user.id && (
        <UserMenu>
          <UserMenuLinkItem>
            <NavLink to="/courses">
              {coursesIcon}
              {t('Your courses')}
            </NavLink>
          </UserMenuLinkItem>

          {COURSES_CATALOG_URL && (
            <UserMenuLinkItem>
              <a href={COURSES_CATALOG_URL} target="_blank" rel="noreferrer">
                {directoryIcon} {t('Courses catalog')}
              </a>
            </UserMenuLinkItem>
          )}

          <AuthComponent
            componentPermissions={[
              ROLES.ROLE_MENTOR,
              ROLES.ROLE_LECTURER,
              ROLES.ROLE_SUPER_ADMIN,
              ROLES.ROLE_CS,
            ]}
          >
            <a id="admin-panel-link" href={`${getBaseUrlForRedirect()}/admin`}>
              {settingsIcon}
            </a>
          </AuthComponent>

          <DesktopMenu>
            <Popover
              defaultShowState={getInitStateForSideMenu()}
              content={
                <UserProfilePopOver>
                  <div className="profile-header">
                    <div>{t('User profile')}</div>
                    <LanguagesContainer>
                      {languages.map((lang) => (
                        <LanguageSelector
                          key={lang}
                          id={`${lang}-lang-button`}
                          isActive={lang === currentLanguage}
                          onClick={() => onChangeLanguage(lang)}
                        >
                          {lang}
                        </LanguageSelector>
                      ))}
                    </LanguagesContainer>
                  </div>
                  <UserProfile user={user} refreshUserData={refreshUserData} />
                </UserProfilePopOver>
              }
            >
              <NavbarIcon id="user-profile-button">{profileIcon}</NavbarIcon>
            </Popover>
          </DesktopMenu>
        </UserMenu>
      )}
    </NavBar>
  );
};

export default withRouter(TopBar);
